import app from 'firebase/app';
import 'firebase/auth';
import 'firebase/firestore';
import 'firebase/storage';
import 'firebase/functions';
import 'firebase/analytics';

const getCredentials = async () => {
  let credentials = undefined;
  if (process.env.NODE_ENV === 'development') {
    if (
      !process.env.REACT_APP_FIREBASE_API_KEY ||
      !process.env.REACT_APP_FIREBASE_TSCRIBE_APP_ID
    ) {
      console.error(
        'no api credentials found, please provide them via .env file'
      );
    } else {
      credentials = {
        apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
        authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
        databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
        projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
        storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
        appId: process.env.REACT_APP_FIREBASE_TSCRIBE_APP_ID,
        measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
      };
    }
  } else {
    const resp = await fetch('/__/firebase/init.json');
    credentials = await resp.json();
    console.debug('credentials retrieved');
  }
  return credentials;
};

let firebaseInstance;

export const FirebaseObj = () => {
  if (!firebaseInstance) {
    console.debug(`firebase instance undefined, creating instance now`);
    firebaseInstance = getFirebase();
  }
  return firebaseInstance;
};

const getFirebase = async () => {
  let credentials = await getCredentials();
  console.debug(`initializing app with credentials: ${credentials}`);
  if (app.apps.length) console.debug(`${app.apps.length} are initialized`);
  app.initializeApp(credentials);
  const auth = app.auth();
  const firestore = app.firestore();
  const storage = app.storage();
  const functions = app.functions();
  const TaskState = app.storage.TaskState;
  firestore.settings({
    cacheSizeBytes: app.firestore.CACHE_SIZE_UNLIMITED
  });
  firestore.enablePersistence({ synchronizeTabs: true }).catch(e => {
    if (e.code === 'failed-precondition') {
      console.error(
        'multiple tabs open, persistance can only be enabled in one tab'
      );
    }
    if (e.code === 'unimplemented') {
      console.error(
        'the current browser does not support all of the features required'
      );
    }
  });
  console.debug(`initialized Firebase now.`);

  return {
    auth,
    firestore,
    storage,
    TaskState,
    /** AUTH API */

    doCreateUserWithEmailAndPassword: (email, password) =>
      auth.createUserWithEmailAndPassword(email, password),

    doSignInWithEmailAndPassword: (email, password) =>
      auth.signInWithEmailAndPassword(email, password),

    doSignOut: () => auth.signOut(),

    doPasswortReset: email => auth.sendPasswordResetEmail(email),

    doPasswordUpdate: password => auth.currentUser.updatePassword(password),

    /** USER API */

    user: uid => firestore.collection('users').doc(uid),

    users: () => firestore.collection('users'),

    /** STORAGE API */

    getStorageRef: () => storage.ref(),

    /** TRANSCRIPT API */

    transcript: id => firestore.collection('transcripts').doc(id),

    transcripts: uid =>
      firestore.collection('transcripts').where('owner', '==', uid),

    deleteTranscript: id => {
      let transcriptsRef = firestore.collection('transcripts').doc(id);
      return transcriptsRef
        .get()
        .then(doc => {
          if (!doc.exists) {
            console.log('No such document!');
          } else {
            console.log('Document data:', doc.data());
            let url = doc.data().url;
            storage.refFromURL(url).delete();
            transcriptsRef.delete();
          }
        })
        .catch(err => {
          console.log('Error getting document', err);
        });
    },

    speechToText: functions.httpsCallable('transcribe')
  };
};

class Firebase {
  constructor() {
    return new Promise(async (resolve, reject) => {
      this.credentials = await getCredentials();
      if (app.apps.length) console.debug(`${app.apps.length} are initialized`);
      app.initializeApp(this.credentials);
      this.projectId = this.credentials.projectId;
      this.auth = app.auth();
      this.firestore = app.firestore();
      this.storage = app.storage();
      this.functions = app.functions();
      this.TaskState = app.storage.TaskState;
      this.analytics = app.analytics();
      this.firestore.settings({
        cacheSizeBytes: app.firestore.CACHE_SIZE_UNLIMITED
      });
      this.firestore.enablePersistence({ synchronizeTabs: true }).catch(e => {
        if (e.code === 'failed-precondition') {
          console.error(
            'multiple tabs open, persistance can only be enabled in one tab'
          );
        }
        if (e.code === 'unimplemented') {
          console.error(
            'the current browser does not support all of the features required'
          );
        }
      });
      console.debug(`initialized Firebase now.`);
      resolve(this);
    });
  }

  /** ANALYTICS API */

  logEvent = (event, eventParams) =>
    this.analytics.logEvent(event, eventParams);

  /** AUTH API */

  doCreateUserWithEmailAndPassword = (email, password) =>
    this.auth.createUserWithEmailAndPassword(email, password);

  doSignInWithEmailAndPassword = (email, password) =>
    this.auth.signInWithEmailAndPassword(email, password);

  doSignOut = () => this.auth.signOut();

  doPasswortReset = email => this.auth.sendPasswordResetEmail(email);

  doPasswordUpdate = password => this.auth.currentUser.updatePassword(password);

  /** USER API */

  user = uid => this.firestore.collection('users').doc(uid);

  users = () => this.firestore.collection('users');

  /** get contingent of current month */
  contingent = uid => {
    var date = new Date();
    var firstDayOfCurrentMonth = new Date(
      date.getFullYear(),
      date.getMonth(),
      1
    );
    var lastDayOfCurrentMonth = new Date(
      date.getFullYear(),
      date.getMonth() + 1,
      0
    );
    return this.firestore
      .collection('users')
      .doc(uid)
      .collection('contingent')
      .where('time', '>=', firstDayOfCurrentMonth)
      .where('time', '<=', lastDayOfCurrentMonth);
  };

  /** STORAGE API */

  getStorageRef = () => this.storage.ref();

  /** TRANSCRIPT API */

  transcript = id => this.firestore.collection('transcripts').doc(id);

  transcripts = uid =>
    this.firestore.collection('transcripts').where('owner', '==', uid);

  deleteTranscript = id => {
    let transcriptsRef = this.firestore.collection('transcripts').doc(id);
    return transcriptsRef
      .get()
      .then(doc => {
        if (!doc.exists) {
          console.log('No such document!');
        } else {
          console.log('Document data:', doc.data());
          let url = doc.data().url;
          this.storage.refFromURL(url).delete();
          transcriptsRef.delete();
        }
      })
      .catch(err => {
        console.log('Error getting document', err);
      });
  };

  /** CONTINGENT API */

  upgradeRequest = uid =>
    this.firestore
      .collection('upgradeRequests')
      .doc()
      .set({
        date: app.firestore.FieldValue.serverTimestamp(),
        user: uid
      });

  /** FUNCTIONS API */

  speechToTextUrl =
    process.env.NODE_ENV === 'development'
      ? 'https://us-central1-autote-staging.cloudfunctions.net/convertTranscript'
      : 'https://us-central1-autote-staging.cloudfunctions.net/convertTranscript';
  // if (process.env.NODE_ENV === 'development') {
  //   console.debug('development env, using functions emulator');
  //   this.functions.useFunctionsEmulator('http://localhost:5001'); // url = "http://localhost:5001/autote-staging/us-central1/transcribe";
  // }
}

export default Firebase;
